import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";

import { axiosInstance } from "../../../../services/v2/axiosInstance";

/*
	Base + velocidad (Actualizaciones v2, 2026-10-29). Llamadas nuevas del módulo, aparte del servicio general para no
	chocar con los otros cambios: qué puede hacer quien mira (antes se pedía `_options` de una alumna cualquiera solo
	para saber si es Caja), la asistencia de una matrícula y los horarios del plantel para el buscador.
*/

export interface Capacidades { campusId: number; isCaja: boolean; isAcademic: boolean; canApply: boolean; canDueDay: boolean; canRecorrer?: boolean; }
/** Recorrer Pagos (Fase 2): lo que pasaría con N meses. */
export interface RecorrerFila { id: number; titulo: string; nuevoTitulo: string; antes: string; despues: string; monto: number; pagado: number; pagada: boolean; }
export interface RecorrerVistaDTO { sugerido: number; canApply: boolean; meses: number; filas: RecorrerFila[]; problemas: string[]; lineas: string[]; whatsappText: string | null; proximoPago: { fecha: string; monto: number } | null; movidas?: number; texto?: string; }
export interface AsistenciaDTO { enrollmentId: number; attendancePct: number | null; exigibleSessions: number | null; lastPresent: string | null; alerts: { code: string; label: string }[]; }
export interface HorarioPlantelDTO {
	id: number; plantel: number; curso: string; modulo: string; periodo: string; horario: string; minuto: number; dias: number[];
	inicio: string; fin: string; maestro: string; aula: string | null; alumnos: number; cupo: number | null; statusId: number; status: string;
}

const base = (campusId: number) => `/admin/campus/${campusId}/enrollmentUpdates`;

export const apiBase = {
	capacidades: (campusId: number): Promise<AxiosResponse<Capacidades>> => axiosInstance.get<Capacidades>(`${base(campusId)}/_capabilities`),
	asistencia: (campusId: number, enrollmentId: number): Promise<AxiosResponse<AsistenciaDTO>> =>
		axiosInstance.get<AsistenciaDTO>(`${base(campusId)}/_attendance?enrollmentId=${enrollmentId}`),
	recorrerVista: (campusId: number, enrollmentId: number, months: number | null, toScheduleId?: number | null): Promise<AxiosResponse<RecorrerVistaDTO>> =>
		axiosInstance.get<RecorrerVistaDTO>(`${base(campusId)}/_recorrer?enrollmentId=${enrollmentId}${months === null ? "" : `&months=${months}`}${toScheduleId ? `&toScheduleId=${toScheduleId}` : ""}`),
	recorrerAplicar: (campusId: number, enrollmentId: number, months: number, note: string): Promise<AxiosResponse<RecorrerVistaDTO>> =>
		axiosInstance.post<RecorrerVistaDTO>(`${base(campusId)}/_recorrer`, { enrollmentId, months, note }),
	horarios: (campusId: number, scope: "vigentes" | "historial"): Promise<AxiosResponse<{ horarios: HorarioPlantelDTO[]; hoy: string }>> =>
		axiosInstance.get(`${base(campusId)}/_schedules?scope=${scope}`)
};

// Una sola petición por plantel mientras dure la pestaña (los roles no cambian sin volver a entrar); si falla, se vuelve a pedir.
const capCache = new Map<number, Promise<Capacidades>>();
export const cargarCapacidades = (campusId: number): Promise<Capacidades> => {
	let p = capCache.get(campusId);
	if (!p) {
		p = apiBase.capacidades(campusId).then((r) => r.data);
		p.catch(() => capCache.delete(campusId));
		capCache.set(campusId, p);
	}
	return p;
};

const NADA: Capacidades = { campusId: 0, isCaja: false, isAcademic: false, canApply: false, canDueDay: false, canRecorrer: false };

/** Capacidades de quien mira en el plantel; mientras llegan, todo en falso (nada se ofrece de más). */
export const useCapacidades = (campusId: number): Capacidades & { listo: boolean } => {
	const [cap, setCap] = useState<Capacidades & { listo: boolean }>({ ...NADA, listo: false });
	useEffect(() => {
		let vigente = true;
		setCap({ ...NADA, listo: false });
		if (!campusId) { setCap({ ...NADA, listo: true }); return; }
		cargarCapacidades(campusId).then((c) => vigente && setCap({ ...c, listo: true })).catch(() => vigente && setCap({ ...NADA, listo: true }));
		return () => { vigente = false; };
	}, [campusId]);
	return cap;
};
